import { ClickToCopy } from "@/components/ClickToCopy";
import { Layout } from "@/components/Layout";
import { LoadingView } from "@/components/Loader";
import { StatusBadge } from "@/components/Status";
import { SearchFilter } from "@/components/table/SelectFilter";
import { DataTable } from "@/components/table/Table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MultiSelectControl } from "@/components/ui/MultiSelectControl";
import {
  DialogTitle,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Case,
  InputDocumentMetadata,
  OutputDocument,
  Clinician,
} from "@/types";
import {
  createCase,
  getCases,
  getClinicians,
  deleteCase,
} from "@/utils/apiCalls";
import { loadAndFormatDate } from "@/utils/dateFormat";
import { useAuthInfo } from "@propelauth/react";
import { PlusIcon, ReloadIcon } from "@radix-ui/react-icons";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";

const createCaseSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  clinicianId: z.string().min(1, "Clinician is required"),
});

const CreateCaseDialog = (props: {
  clinicians: Clinician[];
  onCaseCreated?: (newCase: Case) => void;
}) => {
  const authInfo = useAuthInfo();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof createCaseSchema>>({
    resolver: zodResolver(createCaseSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      clinicianId: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof createCaseSchema>) => {
    setLoading(true);
    const response = await createCase(
      values.firstName,
      values.lastName,
      values.clinicianId,
      authInfo.accessToken ?? null
    );
    if (response !== null) {
      props.onCaseCreated?.(response);
      toast.success("Case created");
      setOpen(false);
      form.reset();
    } else {
      toast.error("Failed to create case, please try again");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Case
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Case</DialogTitle>
          <DialogDescription>
            Enter the applicant's information and select a clinician
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applicant First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="First name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applicant Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clinicianId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Clinician</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a clinician" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {props.clinicians.map((clinician) => (
                        <SelectItem key={clinician.id} value={clinician.id}>
                          {clinician.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? (
                <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <PlusIcon className="h-4 w-4 mr-2" />
              )}
              Create Case
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const CaseTable = (props: {
  data: Case[];
  setCases: React.Dispatch<React.SetStateAction<Case[]>>;
  onCaseDeleted?: () => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
}) => {
  const navigate = useNavigate();
  const authInfo = useAuthInfo();
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async (caseId: string) => {
    setIsLoading(true);
    const response = await deleteCase(caseId, authInfo.accessToken ?? null);
    if (response) {
      toast.success("Case deleted successfully");
      props.onCaseDeleted?.();
    } else {
      toast.error("Failed to delete case");
    }
    setIsLoading(false);
  };

  const columns: ColumnDef<Case>[] = [
    {
      accessorKey: "id",
      header: "ID",
      enableSorting: false,
      meta: {
        headerClassName: "w-[140px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <ClickToCopy
                text={row.original.id}
                className="max-w-[50px] text-ellipsis overflow-hidden whitespace-nowrap"
              />
            </TooltipTrigger>
            <TooltipContent>{row.original.id}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Updated",
      meta: {
        headerClassName: "w-[160px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return <div>{loadAndFormatDate(row.original.updated_at || "")}</div>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      meta: {
        headerClassName: "w-[160px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return <div>{loadAndFormatDate(row.original.created_at || "")}</div>;
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerClassName: "w-[220px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return (
          <div className="max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap">
            {row.original.first_name} {row.original.last_name}
          </div>
        );
      },
    },
    {
      id: "clinician",
      header: "Clinician",
      enableSorting: false,
      meta: {
        headerClassName: "w-[240px]",
      },
      cell: ({ row }: any) => {
        const clinician = row.original.clinician;
        if (!clinician) return <div className="text-muted-foreground">—</div>;

        const displayName = clinician.short_name?.trim() || clinician.name;
        return (
          <Tooltip>
            <TooltipTrigger>
              <div className="max-w-[180px] text-ellipsis overflow-hidden whitespace-nowrap">
                {displayName}
              </div>
            </TooltipTrigger>
            <TooltipContent>{clinician.name}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "input_documents",
      header: "Input Documents",
      enableSorting: false,
      meta: {
        headerClassName: "w-[160px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <Badge>{row.original.input_documents.length}</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto min-w-[200px]">
                {row.original.input_documents.map(
                  (doc: InputDocumentMetadata) => (
                    <div
                      key={doc.id}
                      className="flex flex-row items-center justify-between gap-4"
                    >
                      <div className="text-left">{doc.name}</div>
                      <StatusBadge status={doc.status} />
                    </div>
                  )
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "output_documents",
      header: "Output Documents",
      enableSorting: false,
      meta: {
        headerClassName: "w-[170px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }: any) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <Badge>{row.original.output_documents.length}</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto min-w-[200px]">
                {row.original.output_documents.map((doc: OutputDocument) => (
                  <div
                    key={doc.id}
                    className="flex flex-row items-center justify-between gap-4"
                  >
                    <div className="text-left">{doc.document_type_name}</div>
                    <StatusBadge status={doc.status} />
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: "actions",
      meta: {
        headerClassName: "w-[64px]",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(row.original.id);
                }}
              >
                Delete Case
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable
      data={props.data}
      columns={columns}
      enableSorting
      manualSorting
      sorting={props.sorting}
      onSortingChange={props.onSortingChange}
      tableClassName="table-fixed"
      hidePagination
      onRowClick={(row) => {
        navigate(`/case/${row.original.id}`);
      }}
    />
  );
};

export const AllCasesPage = () => {
  const authInfo = useAuthInfo();
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clinicians, setClinicians] = useState<Clinician[]>([]);
  const [activeClinicianIds, setActiveClinicianIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCases, setTotalCases] = useState(0);
  const [refetchKey, setRefetchKey] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "updated_at", desc: true },
  ]);
  const pageSize = 10;

  useEffect(() => {
    const fetchClinicians = async () => {
      const response = await getClinicians(authInfo.accessToken ?? null);
      if (response) {
        setClinicians(response);
      }
    };
    fetchClinicians();
  }, [authInfo.accessToken]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
    setPage(1); // Reset to first page on sort change
  };

  const getClinicianFilterLabel = (clinician: Clinician) =>
    clinician.short_name?.trim()
      ? `${clinician.short_name} - ${clinician.name}`
      : clinician.name;

  const selectedClinicians = clinicians.filter((clinician) =>
    activeClinicianIds.includes(clinician.id)
  );

  // Derive sort params from TanStack sorting state
  const sortBy = sorting.length > 0 ? sorting[0].id : undefined;
  const sortOrder =
    sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;

  // Fetch cases when page, search, or sort changes
  useEffect(() => {
    const fetchCases = async () => {
      setIsLoading(true);
      const response = await getCases(
        authInfo.accessToken ?? null,
        page,
        pageSize,
        debouncedSearch || undefined,
        sortBy,
        sortOrder,
        activeClinicianIds
      );
      if (response === null) {
        toast.error("Failed to fetch cases");
      } else {
        setCases(response.cases);
        setTotalCases(response.total);
      }
      setIsLoading(false);
    };
    fetchCases();
  }, [
    authInfo.accessToken,
    page,
    debouncedSearch,
    sortBy,
    sortOrder,
    activeClinicianIds,
    refetchKey,
  ]);

  const totalPages = Math.ceil(totalCases / pageSize);

  const handleCaseCreated = (newCase: Case) => {
    const matchesClinicianFilter =
      activeClinicianIds.length === 0 ||
      activeClinicianIds.includes(newCase.clinician.id);

    if (matchesClinicianFilter) {
      // Add the new case and refresh to show it at the top
      setCases((prev) => [newCase, ...prev.slice(0, pageSize - 1)]);
      setTotalCases((prev) => prev + 1);
    }
    setPage(1);
  };

  const handleCaseDeleted = () => {
    // If we're on a page that no longer exists, go to the previous page
    if (page > 1 && cases.length === 1) {
      setPage(page - 1);
    } else {
      // Trigger a refetch
      setRefetchKey((prev) => prev + 1);
    }
  };

  return (
    <Layout title="Cases">
      {isLoading && cases.length === 0 ? (
        <LoadingView text="Loading cases..." />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-md text-muted-foreground">
              {totalCases} {totalCases === 1 ? "case" : "cases"}
            </div>
            <div className="flex items-center space-x-2">
              {clinicians.length > 0 && (
                <MultiSelectControl
                  title="Clinician"
                  items={clinicians.map((clinician) => ({
                    id: clinician.id,
                    name: getClinicianFilterLabel(clinician),
                  }))}
                  selectedItems={selectedClinicians.map((clinician) => ({
                    id: clinician.id,
                    name: getClinicianFilterLabel(clinician),
                  }))}
                  selectItem={(item, isSelected) => {
                    setActiveClinicianIds((prev) =>
                      isSelected
                        ? prev.includes(item.id)
                          ? prev
                          : [...prev, item.id]
                        : prev.filter((id) => id !== item.id)
                    );
                    setPage(1);
                  }}
                  clearSelectedItems={() => {
                    setActiveClinicianIds([]);
                    setPage(1);
                  }}
                  selectAll={() => {
                    setActiveClinicianIds(
                      clinicians.map((clinician) => clinician.id)
                    );
                    setPage(1);
                  }}
                  selectItemOnly={(item) => {
                    setActiveClinicianIds([item.id]);
                    setPage(1);
                  }}
                />
              )}
              <SearchFilter
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search Cases..."
                className={cn("w-[200px]")}
              />
              <CreateCaseDialog
                clinicians={clinicians}
                onCaseCreated={handleCaseCreated}
              />
            </div>
          </div>
          <CaseTable
            data={cases}
            setCases={setCases}
            onCaseDeleted={handleCaseDeleted}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
          {/* Server-side pagination controls */}
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="text-xs text-muted-foreground">
              Page {page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
};
